import { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EventShellProvider, useEventShell } from '../context/EventShellContext';
import { networkManager } from '../api/NetworkManager';
import { Loader2, LogOut, Users as UsersIcon, Brain, Eye, RotateCcw, Sparkles, UserCircle, Menu, X, Home as HomeIcon, ArrowLeft, Calendar, Workflow, MoreHorizontal, Megaphone } from 'lucide-react';

const EMULATABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'organizer', label: 'Organizer' },
  { value: 'participant', label: 'Participant' },
  { value: 'judge', label: 'Judge' },
  { value: 'mentor', label: 'Mentor' },
];

// In-memory cache for event names so we don't refetch on every route change
const eventNameCache = new Map<string, string>();

export function AppLayout() {
  const { user, isAuthenticated, isLoading, logout, can, isSuperAdmin, emulatedRole, emulateRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [eventName, setEventName] = useState<string | null>(null);

  // Detect event context from URL: /events/:eventId/...
  const eventId = useMemo(() => {
    const match = location.pathname.match(/^\/events\/([^/]+)/);
    // "new" is the create page, not an event id
    if (match && match[1] !== 'new') return match[1];
    return null;
  }, [location.pathname]);

  const inEvent = !!eventId;

  // Fetch event name when we enter an event (cached after first fetch)
  useEffect(() => {
    if (!eventId) {
      setEventName(null);
      return;
    }
    if (eventNameCache.has(eventId)) {
      setEventName(eventNameCache.get(eventId) ?? null);
      return;
    }
    let cancelled = false;
    networkManager
      .get<{ status: boolean; data: { name: string } }>(`/api/events/${eventId}`)
      .then((res) => {
        if (cancelled) return;
        eventNameCache.set(eventId, res.data.name);
        setEventName(res.data.name);
      })
      .catch(() => {
        if (cancelled) return;
        setEventName(null);
      });
    return () => { cancelled = true; };
  }, [eventId]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage onLogin={() => navigate('/login')} />;
  }

  const initials = (user?.name ?? user?.yumId ?? '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Helper destinations — promoted to top-level so they're discoverable.
  // Shown as individual nav items on desktop; collapsed into the hamburger menu on mobile.
  const tools = [
    { to: '/mandate', label: 'Mandate', icon: Megaphone, desc: 'The hackathon brief — applies to every event' },
    { to: '/blueprint', label: 'Blueprint', icon: Workflow, desc: 'Product delivery recipe and AI milestones' },
    { to: '/guide', label: 'Guide', icon: Brain, desc: 'Mentor & judge playbook' },
    { to: '/breakthrough', label: 'Breakthrough', icon: Sparkles, desc: 'Mindset prompts for better ideas' },
    { to: '/personas', label: 'Personas', icon: UserCircle, desc: 'Who you\'re building for' },
  ];

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-ring bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-4">
          {/* Left: Logo + context */}
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/" className="flex items-center gap-2 shrink-0" title="Go to home">
              <img src="/logo.svg" alt="Byte Hackathon" className="h-7" />
              {!inEvent && (
                <span className="font-display text-base font-bold text-ink hidden sm:block">Byte Hackathon</span>
              )}
            </Link>

            {/* Event context breadcrumb */}
            {inEvent && (
              <div className="flex min-w-0 items-center gap-1 text-ink">
                <span className="shrink-0 text-ink-tertiary">/</span>
                <Link
                  to={`/events/${eventId}`}
                  className="flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-semibold hover:bg-gray-50"
                  title="Event overview"
                >
                  <Calendar className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
                  <span className="truncate">{eventName ?? 'Event'}</span>
                </Link>
                <button
                  onClick={() => navigate('/')}
                  className="ml-1 hidden shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-ink-tertiary hover:bg-gray-50 hover:text-ink sm:inline-flex"
                  title="Exit event"
                >
                  <ArrowLeft className="h-3 w-3" /> Exit
                </button>
              </div>
            )}
          </div>

          {/* Right: role emulation + tools + user */}
          <div className="flex items-center gap-1.5">
            {isSuperAdmin && !inEvent && (
              <div className="hidden items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1 md:flex">
                <Eye className="h-3.5 w-3.5 text-ink-tertiary" />
                <select
                  value={emulatedRole ?? ''}
                  onChange={(e) => emulateRole(e.target.value || null)}
                  className="border-0 bg-transparent py-0 pr-5 text-xs font-medium text-ink-secondary focus:ring-0"
                >
                  <option value="">Super Admin</option>
                  {EMULATABLE_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>As {r.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Helpers — promoted to top-level nav for discoverability */}
            <nav className="hidden md:flex items-center gap-0.5 mr-1">
              {tools.map((t) => {
                const active = location.pathname.startsWith(t.to);
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    title={t.desc}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                      active ? 'bg-gray-100 text-ink' : 'text-ink-secondary hover:bg-gray-50 hover:text-ink'
                    }`}
                  >
                    <t.icon className="h-4 w-4" />
                    <span className="hidden xl:inline">{t.label}</span>
                  </Link>
                );
              })}
              {can('user:manage') && (
                <Link
                  to="/admin/users"
                  title="Admin: manage platform users"
                  className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin/users') ? 'bg-gray-100 text-ink' : 'text-ink-secondary hover:bg-gray-50 hover:text-ink'
                  }`}
                >
                  <UsersIcon className="h-4 w-4" />
                  <span className="hidden xl:inline">Users</span>
                </Link>
              )}
            </nav>

            {/* Separator between helpers and user chrome */}
            <div className="mx-1 hidden h-5 w-px bg-ring md:block" aria-hidden="true" />

            {/* User avatar */}
            <div className="hidden items-center gap-2 md:flex">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-red-400 to-purple-500 text-xs font-bold text-white">
                {initials}
              </div>
              <span className="hidden text-sm font-medium text-ink max-w-[120px] truncate lg:inline">
                {user?.name || user?.yumId}
              </span>
            </div>
            <button
              onClick={logout}
              className="hidden md:flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-secondary hover:bg-gray-50 hover:text-ink transition-colors"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden rounded-lg p-2 text-ink-secondary hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="border-t border-ring bg-white px-4 py-3 md:hidden">
            <div className="flex items-center gap-3 pb-3 border-b border-ring mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-red-400 to-purple-500 text-sm font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink truncate">{user?.name || user?.yumId}</p>
                <p className="text-xs text-ink-tertiary truncate">{user?.email}</p>
              </div>
            </div>

            {inEvent && (
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="mb-2 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-ink-secondary"
              >
                <ArrowLeft className="h-4 w-4" /> Exit event
              </Link>
            )}

            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === '/' ? 'bg-gray-100 text-ink' : 'text-ink-secondary hover:bg-gray-50'
              }`}
            >
              <HomeIcon className="h-4 w-4" /> Home
            </Link>

            <div className="mt-3 border-t border-ring pt-3">
              <p className="mb-1 px-3 text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">Tools</p>
              {tools.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    location.pathname.startsWith(t.to) ? 'bg-gray-100 text-ink' : 'text-ink-secondary hover:bg-gray-50'
                  }`}
                >
                  <t.icon className="h-4 w-4" /> {t.label}
                </Link>
              ))}
            </div>

            {can('user:manage') && (
              <Link
                to="/admin/users"
                onClick={() => setMobileMenuOpen(false)}
                className={`mt-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium border-t border-ring pt-3 ${
                  location.pathname.startsWith('/admin/users') ? 'bg-gray-100 text-ink' : 'text-ink-secondary hover:bg-gray-50'
                }`}
              >
                <UsersIcon className="h-4 w-4" /> Users
              </Link>
            )}

            {isSuperAdmin && (
              <div className="mt-3 border-t border-ring pt-3">
                <div className="flex items-center gap-2 px-3 py-2">
                  <Eye className="h-4 w-4 text-ink-tertiary" />
                  <select
                    value={emulatedRole ?? ''}
                    onChange={(e) => { emulateRole(e.target.value || null); setMobileMenuOpen(false); }}
                    className="flex-1 rounded-lg border border-ring bg-white px-2 py-1.5 text-sm text-ink"
                  >
                    <option value="">View as Super Admin</option>
                    {EMULATABLE_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>View as {r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="mt-3 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        )}
      </header>

      {/* Emulation banner */}
      {emulatedRole && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2">
          <div className="mx-auto flex max-w-7xl items-center justify-between">
            <p className="text-sm font-medium text-amber-800">
              <Eye className="mr-1.5 inline h-4 w-4" />
              Viewing as <strong>{emulatedRole}</strong>
            </p>
            <button
              onClick={() => emulateRole(null)}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-200"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        <EventShellProvider>
          <Outlet />
          {inEvent && eventId && <MobileEventNav eventId={eventId} />}
        </EventShellProvider>
      </main>
    </div>
  );
}

function MobileEventNav({ eventId }: { eventId: string }) {
  const { tabs } = useEventShell();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') ?? 'overview';

  if (tabs.length === 0) return null;

  // Up to 4 tabs get a direct slot; the rest go behind a "More" sheet so 5+ still fits.
  const primary = tabs.slice(0, 4);
  const overflow = tabs.slice(4);
  const activeInOverflow = overflow.some((t) => t.id === activeTab);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-ring bg-white/95 backdrop-blur-md md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Event navigation"
    >
      <div className="flex items-stretch justify-around">
        {primary.map((t) => {
          const active = activeTab === t.id;
          const to = t.id === 'overview' ? `/events/${eventId}` : `/events/${eventId}?tab=${t.id}`;
          return (
            <MobileNavItem
              key={t.id}
              as={Link}
              to={to}
              icon={t.icon}
              label={t.label}
              active={active}
            />
          );
        })}
        {overflow.length > 0 && (
          <MobileMoreTabs
            tabs={overflow}
            active={activeInOverflow}
            onSelect={(id) => navigate(id === 'overview' ? `/events/${eventId}` : `/events/${eventId}?tab=${id}`)}
          />
        )}
      </div>
    </nav>
  );
}

// Unified tab-bar item visual: larger tap target, rounded pill behind the icon when active.
// Works with either a Link (`as={Link}`) or a button by swapping the rendered element.
function MobileNavItem({
  as: Component = 'button',
  to,
  onClick,
  icon: Icon,
  label,
  active,
  ariaLabel,
}: {
  as?: typeof Link | 'button';
  to?: string;
  onClick?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  ariaLabel?: string;
}) {
  const content = (
    <>
      <span
        className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${
          active ? 'bg-accent-subtle' : ''
        }`}
      >
        <Icon className={`h-5 w-5 ${active ? 'text-ink' : 'text-ink-tertiary'}`} />
      </span>
      <span
        className={`max-w-full truncate leading-none ${
          active ? 'text-ink' : 'text-ink-tertiary'
        }`}
      >
        {label}
      </span>
    </>
  );
  const className =
    'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 pb-1.5 pt-1 text-2xs font-medium transition-colors';
  if (Component === 'button') {
    return (
      <button type="button" className={className} onClick={onClick} aria-label={ariaLabel} aria-current={active ? 'page' : undefined}>
        {content}
      </button>
    );
  }
  return (
    <Link to={to ?? '#'} className={className} aria-current={active ? 'page' : undefined}>
      {content}
    </Link>
  );
}

function MobileMoreTabs({
  tabs,
  active,
  onSelect,
}: {
  tabs: Array<{ id: string; label: string; icon: React.ComponentType<{ className?: string }> }>;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);

  // Dismiss on ESC and lock body scroll while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handler);
    };
  }, [open]);

  return (
    <>
      <MobileNavItem
        as="button"
        onClick={() => setOpen(true)}
        icon={MoreHorizontal}
        label="More"
        active={active}
        ariaLabel="Show more event tabs"
      />
      {open && (
        <div
          className="fixed inset-0 z-50 animate-fade-in bg-black/40"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="More event tabs"
        >
          <div
            className="absolute inset-x-0 bottom-0 animate-slide-up rounded-t-2xl bg-white shadow-float"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-ring px-4 pt-3 pb-2">
              <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">More tabs</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 text-ink-tertiary hover:bg-surface-sunken hover:text-ink"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-2 pt-2">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    onSelect(t.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-ink hover:bg-surface-sunken"
                >
                  <t.icon className="h-4 w-4 text-ink-tertiary" /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LandingPage({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface p-4">
      <div className="text-center">
        <img src="/logo.svg" alt="Byte Hackathon" className="mx-auto h-16" />
        <h1 className="mt-4 font-display text-3xl font-bold text-ink">Byte Hackathon</h1>
        <p className="mt-2 text-sm text-ink-secondary">AI-native innovation platform</p>
        <button onClick={onLogin} className="btn-primary mt-6">
          Sign in with Yum! ID
        </button>
      </div>
    </div>
  );
}
